/*
|-------------------------------------------------------------------------------
| VUEX store.js
|-------------------------------------------------------------------------------
| Builds the data store from all of the modules for the Xu Yuan Web App.
*/
/*
  Adds the promise polyfill for IE 11
*/
require('es6-promise').polyfill()

/*
    Imports Vue and Vuex
*/
import Vue from 'vue'
import Vuex from 'vuex'

/*
    Initializes Vuex on Vue.
*/
Vue.use(Vuex)

import { student } from './modules/student'
import { teacher } from './modules/teacher'
import { lessonPlan } from './modules/lessonPlan'
import { storage } from './modules/storage'
import { lessonCategory } from './modules/lessonCategory'
import { subscriptionPackage } from './modules/subscriptionPackage'
import { subscriptionOrder } from './modules/subscriptionOrder'
import { invoice } from './modules/invoice'
import { classroom } from './modules/classroom'
import { classroomSchedule } from './modules/classroomSchedule'
import { lesson } from './modules/lesson'
import { curriculum } from './modules/curriculum'
import { studyProgress } from './modules/studyProgress'
import { dashboard } from './modules/dashboard'
import { payroll } from './modules/payroll'
import { employee } from './modules/employee'
import { role } from './modules/role'
import { report } from './modules/report'
import { holiday } from './modules/holiday'

/*
  Exports our data store.
*/
export default new Vuex.Store({
  state: {
    auth: {
      user: {},
      roles: [],
      permissions: {},
    },
    accountMenus: [
      {
        title: 'My Profile',
        route: null,
      },
      {
        title: 'Logout',
        route: 'logout',
      },
    ],
    navItems: [
      {
        title: 'Dashboard',
        icon: 'mdi-chart-line',
        permission: 'dashboard',
        allowedAccounts: ['employee', 'teacher', 'student'],
        route: 'dashboard.index',
        path: 'dashboard',
      },
      {
        title: 'Student',
        icon: 'mdi-school',
        permission: 'students',
        allowedAccounts: ['employee'],
        route: 'students.index',
        path: 'students',
      },
      {
        title: 'Teacher',
        icon: 'mdi-human-male-board-poll',
        permission: 'teachers',
        allowedAccounts: ['employee'],
        route: 'teachers.index',
        path: 'teachers',
      },
      {
        title: 'Lesson',
        icon: 'mdi-ideogram-cjk',
        allowedAccounts: ['employee'],
        subMenus: [
          {
            title: 'Lesson Plan',
            permission: 'lesson-plans',
            allowedAccounts: ['employee'],
            route: 'lesson-plans.index',
            path: 'lesson-plans',
          },
          {
            title: 'Lesson Categories',
            permission: 'lesson-plans',
            allowedAccounts: ['employee'],
            route: 'lesson-categories.index',
            path: 'lesson-categories',
          },
        ],
      },
      {
        title: 'Subscription',
        icon: 'mdi-sale',
        allowedAccounts: ['employee'],
        subMenus: [
          {
            title: 'Subscription Package',
            permission: 'subscription-packages',
            allowedAccounts: ['employee'],
            route: 'subscription-packages.index',
            path: 'subscription-packages',
          },
          {
            title: 'Subscription Order',
            permission: 'subscription-orders',
            allowedAccounts: ['employee'],
            route: 'subscription-orders.index',
            path: 'subscription-orders',
          },
        ],
      },
      {
        title: 'Invoice',
        icon: 'mdi-currency-usd',
        permission: 'invoices',
        allowedAccounts: ['employee', 'student'],
        route: 'invoices.index',
        path: 'invoices',
      },
      {
        title: 'Classroom',
        icon: 'mdi-android-studio',
        permission: 'classrooms',
        allowedAccounts: ['employee', 'student', 'teacher'],
        route: 'classrooms.index',
        path: 'classrooms',
      },
      {
        title: 'Class Schedule',
        icon: 'mdi-calendar-clock',
        permission: 'classroom-schedules',
        allowedAccounts: ['employee', 'student', 'teacher'],
        route: 'classroom-schedules.index',
        path: 'classroom-schedules',
      },
      {
        title: 'Holiday',
        icon: 'mdi-beach',
        permission: 'holidays',
        allowedAccounts: ['employee'],
        route: 'holidays.index',
        path: 'holidays',
      },
      {
        title: 'Study Report',
        icon: 'mdi-script-text',
        permission: 'study-progress',
        allowedAccounts: ['employee', 'student'],
        route: 'study-progress.monthly-reports.index',
        path: 'study-progress.monthly-reports',
      },
      {
        title: 'Payroll',
        icon: 'mdi-account-cash',
        permission: 'payrolls',
        allowedAccounts: ['employee', 'teacher'],
        route: 'payrolls.index',
        path: 'payrolls',
      },
      {
        title: 'Report & Monitoring',
        icon: 'mdi-file-chart',
        allowedAccounts: ['employee'],
        subMenus: [
          {
            title: 'Invoice Monitoring',
            permission: 'dashboard',
            allowedAccounts: ['employee'],
            route: 'reports.invoice-monitoring',
            path: 'reports.invoice-monitoring',
          },
          {
            title: 'Classroom Monitoring',
            permission: 'dashboard',
            allowedAccounts: ['employee'],
            route: 'reports.classroom-monitoring',
            path: 'reports.classroom-monitoring',
          },
        ],
      },
      {
        title: 'Admin',
        icon: 'mdi-account-group',
        permission: 'employees',
        allowedAccounts: ['employee'],
        route: 'employees.index',
        path: 'employees',
      },
    ],
    apiError: {},
    alerts: [],
  },
  getters: {
    auth: (state) => {
      return state.auth
    },
    authUser: (state) => {
      return state.auth.user
    },
    accountType: (state, getters) => {
      return getters.authUser.account_type
    },
    isTeacherAccount: (state, getters) => {
      return getters.accountType === 'teacher'
    },
    isStudentAccount: (state, getters) => {
      return getters.accountType === 'student'
    },
    isEmployeeAccount: (state, getters) => {
      return getters.accountType === 'employee'
    },
    accountMenus: (state) => {
      return state.accountMenus
    },
    navItems: (state) => {
      return state.navItems
    },
    authorizedNavItems(state, getters) {
      const result = []
      for (const navItem of state.navItems) {
        const isAllowed = navItem.allowedAccounts.includes(getters.accountType)
        if (!isAllowed) {
          continue
        }
        if (navItem.subMenus && navItem.subMenus.length > 0) {
          const subMenus = navItem.subMenus.filter((subMenu) => {
            const isHasPermission =
              state.auth.permissions[subMenu.permission].view
            const isSubMenuAllowed = subMenu.allowedAccounts.includes(
              getters.accountType
            )
            return isHasPermission && isSubMenuAllowed
          })
          if (subMenus && subMenus.length > 0) {
            result.push({ ..._.omit(navItem, 'subMenus'), subMenus })
          }
        } else if (state.auth.permissions[navItem.permission].view) {
          result.push(navItem)
        }
      }
      return result
    },
    activeNavItem(state, getters) {
      const result = getters.authorizedNavItems.find((navItem) => {
        if (navItem.subMenus && navItem.subMenus.length > 0) {
          return navItem.subMenus.find((subMenu) =>
            route().current().startsWith(subMenu.path)
          )
        } else {
          return route().current().startsWith(navItem.path)
        }
      })
      return result
    },
    activeNavItemIdx(state, getters) {
      const result = getters.authorizedNavItems.findIndex((navItem) => {
        if (navItem.subMenus && navItem.subMenus.length > 0) {
          return navItem.subMenus.find((subMenu) =>
            route().current().startsWith(subMenu.path)
          )
        } else {
          return route().current().startsWith(navItem.path)
        }
      })
      return result
    },
    hasPermission: (state) => (permission) => {
      const parts = permission.split('.')
      return (
        state.auth.permissions[parts[0]] &&
        state.auth.permissions[parts[0]][parts[1]]
      )
    },
    apiError: (state) => {
      return state.apiError
    },
    alerts: (state) => {
      return state.alerts
    },
    getApiError: (state) => (key) => {
      return state.apiError[key]
    },
  },
  mutations: {
    setApiError(state, { key, data }) {
      state.apiError[key] = data
    },
    addAlert(state, data) {
      state.alerts.push(data)
    },
    removeAlert(state, index) {
      if (state.alerts && state.alerts.length > 0) {
        state.alerts.splice(index, 1)
      }
    },
    setAuth(state, data) {
      state.auth = data
    },
  },
  actions: {
    handleError({ commit, dispatch }, { key, error }) {
      console.log(error)
      if (error.isAxiosError) {
        const response = error.response
        if (response && response.data) {
          commit('setApiError', { key, data: response.data })
          dispatch('showErrorAlert', response.data.error_message)
        }
      }
    },
    showErrorAlert({ commit }, message) {
      commit('addAlert', {
        type: 'error',
        message: message,
      })
    },
    showSuccessAlert({ commit }, message) {
      commit('addAlert', {
        type: 'success',
        message: message,
      })
    },
  },
  modules: {
    storage,
    student,
    teacher,
    lessonPlan,
    lessonCategory,
    subscriptionPackage,
    subscriptionOrder,
    invoice,
    classroom,
    classroomSchedule,
    lesson,
    curriculum,
    studyProgress,
    dashboard,
    payroll,
    employee,
    role,
    report,
    holiday,
  },
})
